/**
 * UI 부트스트랩 설정 계약 (ADR 0025 · Issue #13)
 * --------------------------------------------------------------------------
 * `GET /api/v1/ui-config`는 **운영자가 정한 표시 언어 기본값**만 돌려줍니다.
 *
 * 규칙
 * - **권한 판정이 아닙니다.** 인증 경계는 `/api/v1/*`의 기존 규칙 그대로이고,
 *   응답에 Scope·역할·주체·내부 주소·데이터소스 설정이 들어가지 않습니다.
 * - **요청 언어에 따라 값이 달라지지 않습니다.** 서버 응답은 locale-invariant이며
 *   `Accept-Language`는 이 응답을 바꾸지 않습니다. 언어 선택은 브라우저가 합니다.
 * - 지원 목록은 **닫힌 집합**입니다. 목록 밖의 값은 기본값이 될 수 없습니다.
 *
 * Go 정본은 apps/api/internal/contract/uiconfig.go,
 * OpenAPI 정본은 packages/contracts/openapi.yaml입니다.
 */

/** 이 제품이 배포하는 표시 언어. 목록 밖 언어는 존재하지 않는 것으로 취급합니다. */
export const SUPPORTED_LOCALES = ["ko", "en"] as const;

export type Locale = (typeof SUPPORTED_LOCALES)[number];

/** 제품 기본 언어. 운영자 기본값이 없거나 유효하지 않을 때의 최종 낙하점입니다. */
export const DEFAULT_LOCALE: Locale = "ko";

/** 메시지가 없는 ID를 채우는 언어. 카탈로그 parity 게이트가 공백 자체를 막습니다. */
export const FALLBACK_LOCALE: Locale = "en";

/** `GET /api/v1/ui-config` 응답. 필드가 둘뿐인 것이 이 계약의 핵심입니다. */
export interface UIConfigResponse {
  /** 이 배포가 제공하는 언어. 항상 1개 이상이며 중복이 없습니다. */
  supportedLocales: Locale[];
  /** 운영자가 정한 기본 언어. 반드시 supportedLocales 안에 있습니다. */
  defaultLocale: Locale;
}

export function isSupportedLocale(value: unknown): value is Locale {
  return typeof value === "string" && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/**
 * 서버 응답을 엄격하게 검증합니다. 모르는 필드·모르는 언어·목록 밖 기본값은
 * 조용히 통과시키지 않습니다 — 부트스트랩이 흔들리면 화면 전체 문구가 흔들립니다.
 */
export function parseUIConfig(value: unknown): UIConfigResponse {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error("invalid ui-config");
  const record = value as Record<string, unknown>;
  if (Object.keys(record).sort().join(",") !== "defaultLocale,supportedLocales") throw new Error("invalid ui-config");
  const supported = record.supportedLocales;
  if (!Array.isArray(supported) || supported.length === 0 || supported.length > SUPPORTED_LOCALES.length) {
    throw new Error("invalid ui-config");
  }
  const locales: Locale[] = [];
  for (const entry of supported) {
    if (!isSupportedLocale(entry) || locales.includes(entry)) throw new Error("invalid ui-config");
    locales.push(entry);
  }
  const fallback = record.defaultLocale;
  if (!isSupportedLocale(fallback) || !locales.includes(fallback)) throw new Error("invalid ui-config");
  return { supportedLocales: locales, defaultLocale: fallback };
}
